import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useDispatch } from 'react-redux';
import { verifyResetToken, resetPassword } from '../../slice/authSlice';
import Swal from 'sweetalert2';

// Validation Schema
const resetSchema = yup.object().shape({
    password: yup
        .string()
        .required('Please enter a new password')
        .min(8, 'Password must be at least 8 characters')
        .matches(/[A-Z]/, 'Must contain at least one uppercase letter')
        .matches(/[0-9]/, 'Must contain at least one number'),
    confirmPassword: yup
        .string()
        .required('Please confirm your password')
        .oneOf([yup.ref('password')], 'Passwords do not match'),
});

// Password strength helper
const getStrength = (pwd) => {
    if (!pwd) return { score: 0, label: '', color: '' };
    let score = 0;
    if (pwd.length >= 8) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    const map = [
        { label: '', color: '' },
        { label: 'Weak', color: '#f87171' },
        { label: 'Fair', color: '#fb923c' },
        { label: 'Good', color: '#facc15' },
        { label: 'Strong', color: '#4ade80' },
    ];
    return { score, ...map[score] };
};

const EyeIcon = ({ open }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {open ? (
            <>
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                <line x1="1" y1="1" x2="23" y2="23" />
            </>
        ) : (
            <>
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
            </>
        )}
    </svg>
);

const ResetPass = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    
    // States
    const [tokenStatus, setTokenStatus] = useState('verifying'); // 'verifying' | 'valid' | 'expired' | 'invalid'
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [resetDone, setResetDone] = useState(false);
    const [watchedPassword, setWatchedPassword] = useState('');
    console.log(tokenStatus,"tokenStatus");
    
    const { 
        register,
        handleSubmit,
        watch,
        formState: { errors, isSubmitting },
    } = useForm({
        resolver: yupResolver(resetSchema),
        mode: 'onBlur',
    });

    const passwordValue = watch('password', '');
    const strength = getStrength(passwordValue);

    // ── Verify token on mount ──
    useEffect(() => {
        if (!token) {
            setTokenStatus('invalid');
            return;
        }
        const verify = async () => {
            try {
                const response = await dispatch(verifyResetToken({ token }));
                if (response?.payload?.code === 200) {
                    setTokenStatus('valid');
                } else {
                    // Differentiate expired vs invalid based on message if available
                    const msg = response?.payload?.message?.toLowerCase() || '';
                    setTokenStatus(msg.includes('expire') ? 'expired' : 'invalid');
                }
            } catch {
                setTokenStatus('invalid');
            }
        };
        verify();
    }, [token, dispatch]);

    // ── Submit reset ──
    const onSubmit = async (data) => {
        try {
            const response = await dispatch(
                resetPassword({
                    payload: {
                        token,
                        new_password: data.password,
                        confirm_password: data.confirmPassword,
                    },
                })
            );
            if (response?.payload?.code === 200) {
                setResetDone(true);
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    backdrop: false,
                    title: response?.payload?.message || 'Password reset successfully!',
                    showConfirmButton: false,
                    timer: 3000,
                });
            } else {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'error',
                    backdrop: false,
                    title: response?.payload?.message || 'Something went wrong. Please try again.',
                    showConfirmButton: false,
                    timer: 3000,
                });
            }
        } catch (error) {
            console.error('Reset password error:', error);
        }
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');

                .rp-wrapper {
                    min-height: 100vh;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-family: 'DM Sans', sans-serif;
                    background: #0d0d14;
                    position: relative;
                    overflow: hidden;
                    padding: 20px;
                }

                /* Ambient background orbs */
                .rp-orb {
                    position: absolute;
                    border-radius: 50%;
                    filter: blur(80px);
                    pointer-events: none;
                    z-index: 0;
                }
                .rp-orb-1 {
                    width: 420px; height: 420px;
                    background: radial-gradient(circle, rgba(99,102,241,0.18) 0%, transparent 70%);
                    top: -120px; right: -80px;
                    animation: float1 8s ease-in-out infinite;
                }
                .rp-orb-2 {
                    width: 320px; height: 320px;
                    background: radial-gradient(circle, rgba(16,185,129,0.12) 0%, transparent 70%);
                    bottom: -80px; left: -60px;
                    animation: float2 10s ease-in-out infinite;
                }
                .rp-orb-3 {
                    width: 200px; height: 200px;
                    background: radial-gradient(circle, rgba(244,114,182,0.1) 0%, transparent 70%);
                    top: 50%; left: 30%;
                    animation: float1 12s ease-in-out infinite reverse;
                }

                @keyframes float1 {
                    0%, 100% { transform: translateY(0px) scale(1); }
                    50% { transform: translateY(-30px) scale(1.05); }
                }
                @keyframes float2 {
                    0%, 100% { transform: translateY(0px) scale(1); }
                    50% { transform: translateY(20px) scale(0.95); }
                }

                .rp-card {
                    background: rgba(255, 255, 255, 0.04);
                    backdrop-filter: blur(24px);
                    -webkit-backdrop-filter: blur(24px);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 24px;
                    padding: 44px 40px;
                    width: 100%;
                    max-width: 440px;
                    box-shadow:
                        0 32px 64px rgba(0, 0, 0, 0.4),
                        inset 0 1px 0 rgba(255,255,255,0.06);
                    position: relative;
                    z-index: 1;
                    animation: cardIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }

                @keyframes cardIn {
                    from { opacity: 0; transform: translateY(40px) scale(0.97); }
                    to   { opacity: 1; transform: translateY(0)   scale(1); }
                }

                /* ── Icon circles ── */
                .rp-icon-circle {
                    width: 68px; height: 68px;
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 22px;
                }
                .rp-icon-circle.purple {
                    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
                    box-shadow: 0 10px 30px rgba(99,102,241,0.45);
                }
                .rp-icon-circle.red {
                    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
                    box-shadow: 0 10px 30px rgba(239,68,68,0.4);
                }
                .rp-icon-circle.green {
                    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                    box-shadow: 0 10px 30px rgba(16,185,129,0.45);
                }
                .rp-icon-circle svg {
                    width: 30px; height: 30px;
                    fill: none; stroke: #fff;
                    stroke-width: 2; stroke-linecap: round; stroke-linejoin: round;
                }
                .rp-icon-circle.green svg {
                    stroke-dasharray: 60;
                    animation: checkDraw 0.5s ease 0.2s both;
                }
                @keyframes checkDraw {
                    from { stroke-dashoffset: 60; }
                    to   { stroke-dashoffset: 0; }
                }

                /* Spinner inside icon circle */
                .rp-spinner {
                    width: 28px; height: 28px;
                    border: 3px solid rgba(255,255,255,0.2);
                    border-top-color: #fff;
                    border-radius: 50%;
                    animation: spin 0.8s linear infinite;
                }
                @keyframes spin { to { transform: rotate(360deg); } }

                /* ── Typography ── */
                .rp-title {
                    font-size: 1.55rem;
                    font-weight: 700;
                    color: #f1f5f9;
                    letter-spacing: -0.4px;
                    margin-bottom: 6px;
                }
                .rp-subtitle {
                    font-size: 0.875rem;
                    color: rgba(255,255,255,0.45);
                    line-height: 1.6;
                    margin-bottom: 0;
                }

                /* ── Form elements ── */
                .rp-label {
                    font-size: 0.78rem;
                    font-weight: 600;
                    color: rgba(255,255,255,0.6);
                    letter-spacing: 0.6px;
                    text-transform: uppercase;
                    margin-bottom: 8px;
                    display: block;
                }
                .rp-input-wrap {
                    position: relative;
                }
                .rp-input {
                    background: rgba(255,255,255,0.06) !important;
                    border: 1.5px solid rgba(255,255,255,0.1) !important;
                    border-radius: 12px !important;
                    color: #f1f5f9 !important;
                    padding: 13px 48px 13px 16px !important;
                    font-size: 0.92rem !important;
                    font-family: 'DM Sans', sans-serif !important;
                    width: 100%;
                    transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease !important;
                    height: auto !important;
                }
                .rp-input::placeholder { color: rgba(255,255,255,0.25) !important; }
                .rp-input:focus {
                    background: rgba(255,255,255,0.09) !important;
                    border-color: #6366f1 !important;
                    box-shadow: 0 0 0 3px rgba(99,102,241,0.2) !important;
                    outline: none !important;
                }
                .rp-input.has-error {
                    border-color: #f87171 !important;
                    box-shadow: 0 0 0 3px rgba(248,113,113,0.12) !important;
                }
                .rp-eye-btn {
                    position: absolute;
                    right: 14px; top: 50%;
                    transform: translateY(-50%);
                    background: none; border: none; padding: 0;
                    color: rgba(255,255,255,0.35);
                    cursor: pointer;
                    transition: color 0.2s ease;
                    display: flex; align-items: center;
                }
                .rp-eye-btn:hover { color: rgba(255,255,255,0.7); }

                .rp-error {
                    font-size: 0.775rem;
                    color: #f87171;
                    margin-top: 6px;
                    display: flex; align-items: center; gap: 5px;
                }
                .rp-error svg {
                    flex-shrink: 0;
                    stroke: #f87171; fill: none;
                    stroke-width: 2.5; stroke-linecap: round;
                }

                /* ── Password strength bar ── */
                .strength-wrap { margin-top: 10px; }
                .strength-bars {
                    display: flex; gap: 5px; margin-bottom: 5px;
                }
                .strength-bar {
                    height: 3px; flex: 1; border-radius: 99px;
                    background: rgba(255,255,255,0.1);
                    transition: background 0.3s ease;
                }
                .strength-label {
                    font-size: 0.72rem;
                    font-family: 'DM Mono', monospace;
                    letter-spacing: 0.3px;
                    transition: color 0.3s ease;
                }

                /* ── Requirements checklist ── */
                .req-list {
                    background: rgba(255,255,255,0.03);
                    border: 1px solid rgba(255,255,255,0.07);
                    border-radius: 10px;
                    padding: 12px 14px;
                    margin-top: 10px;
                    list-style: none;
                    margin-bottom: 0;
                    display: flex; flex-direction: column; gap: 5px;
                }
                .req-item {
                    font-size: 0.78rem;
                    color: rgba(255,255,255,0.35);
                    display: flex; align-items: center; gap: 7px;
                    transition: color 0.2s ease;
                }
                .req-item.met { color: #4ade80; }
                .req-dot {
                    width: 6px; height: 6px; border-radius: 50%;
                    background: rgba(255,255,255,0.2);
                    flex-shrink: 0;
                    transition: background 0.2s ease;
                }
                .req-item.met .req-dot { background: #4ade80; }

                /* ── Submit button ── */
                .rp-btn {
                    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%) !important;
                    border: none !important;
                    border-radius: 12px !important;
                    padding: 14px !important;
                    font-size: 0.93rem !important;
                    font-weight: 600 !important;
                    font-family: 'DM Sans', sans-serif !important;
                    color: #fff !important;
                    width: 100%;
                    letter-spacing: 0.2px;
                    transition: all 0.25s ease !important;
                    box-shadow: 0 4px 20px rgba(99,102,241,0.4) !important;
                    position: relative;
                    overflow: hidden;
                }
                .rp-btn::before {
                    content: '';
                    position: absolute; inset: 0;
                    background: linear-gradient(135deg, rgba(255,255,255,0.1), transparent);
                    opacity: 0;
                    transition: opacity 0.2s ease;
                }
                .rp-btn:hover:not(:disabled)::before { opacity: 1; }
                .rp-btn:hover:not(:disabled) {
                    transform: translateY(-2px) !important;
                    box-shadow: 0 8px 30px rgba(99,102,241,0.55) !important;
                }
                .rp-btn:disabled { opacity: 0.65 !important; cursor: not-allowed !important; }
                .btn-inner-spinner {
                    display: inline-block;
                    width: 14px; height: 14px;
                    border: 2px solid rgba(255,255,255,0.3);
                    border-top-color: #fff;
                    border-radius: 50%;
                    animation: spin 0.7s linear infinite;
                    margin-right: 8px; vertical-align: middle;
                }

                /* ── Divider & back link ── */
                .rp-divider {
                    border-color: rgba(255,255,255,0.07) !important;
                    margin: 24px 0 !important;
                }
                .rp-back {
                    color: rgba(255,255,255,0.45) !important;
                    text-decoration: none !important;
                    font-size: 0.86rem;
                    display: inline-flex; align-items: center; gap: 6px;
                    transition: color 0.2s ease;
                }
                .rp-back:hover { color: #a5b4fc !important; }
                .rp-back svg {
                    width: 14px; height: 14px;
                    stroke: currentColor; fill: none;
                    stroke-width: 2; stroke-linecap: round; stroke-linejoin: round;
                    transition: transform 0.2s ease;
                }
                .rp-back:hover svg { transform: translateX(-3px); }

                /* ── Token expired/invalid card ── */
                .rp-expired-badge {
                    background: rgba(239,68,68,0.1);
                    border: 1px solid rgba(239,68,68,0.25);
                    border-radius: 10px;
                    padding: 12px 16px;
                    font-size: 0.83rem;
                    color: #fca5a5;
                    line-height: 1.5;
                    margin: 16px 0;
                }

                /* ── Success state ── */
                .rp-success-email {
                    background: rgba(16,185,129,0.1);
                    border: 1px solid rgba(16,185,129,0.25);
                    border-radius: 10px;
                    padding: 11px 15px;
                    font-size: 0.85rem;
                    color: #6ee7b7;
                    margin: 14px 0;
                    font-family: 'DM Mono', monospace;
                }

                .logo-wrap img { height: 48px; object-fit: contain; }

                /* Verifying pulse */
                .rp-verify-dots span {
                    display: inline-block;
                    width: 6px; height: 6px;
                    background: rgba(255,255,255,0.4);
                    border-radius: 50%;
                    margin: 0 3px;
                    animation: dotPulse 1.4s ease-in-out infinite;
                }
                .rp-verify-dots span:nth-child(2) { animation-delay: 0.2s; }
                .rp-verify-dots span:nth-child(3) { animation-delay: 0.4s; }
                @keyframes dotPulse {
                    0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
                    40% { opacity: 1; transform: scale(1); }
                }
            `}</style>

            <div className="rp-wrapper">
                {/* Ambient orbs */}
                <div className="rp-orb rp-orb-1" />
                <div className="rp-orb rp-orb-2" />
                <div className="rp-orb rp-orb-3" />

                <div className="rp-card">
                    {/* Logo */}
                    <div className="text-center mb-4 logo-wrap">
                        <Link to="/">
                            <img
                                src={process.env.PUBLIC_URL + '/assets/images/logo-new.svg'}
                                alt="Logo"
                            />
                        </Link>
                    </div>

                    {/* ── STATE: Verifying Token ── */}
                    {tokenStatus === 'verifying' && (
                        <div className="text-center">
                            <div className="rp-icon-circle purple">
                                <div className="rp-spinner" />
                            </div>
                            <h4 className="rp-title">Verifying Link</h4>
                            <p className="rp-subtitle">
                                Checking your reset link, please wait
                                <span className="rp-verify-dots ms-1">
                                    <span /><span /><span />
                                </span>
                            </p>
                        </div>
                    )}

                    {/* ── STATE: Token Expired ── */}
                    {tokenStatus === 'expired' && (
                        <div className="text-center">
                            <div className="rp-icon-circle red">
                                <svg viewBox="0 0 24 24">
                                    <circle cx="12" cy="12" r="10" />
                                    <polyline points="12 6 12 12 16 14" />
                                </svg>
                            </div>
                            <h4 className="rp-title">Link Expired</h4>
                            <p className="rp-subtitle">This password reset link has expired.</p>
                            <div className="rp-expired-badge">
                                Reset links are valid for a limited time only. Please request a new one to continue.
                            </div>
                            <Link to="/forget-password" className="rp-btn d-block text-center text-decoration-none mt-3"
                                style={{ padding: '13px', borderRadius: '12px' }}>
                                Request New Link
                            </Link>
                            <hr className="rp-divider" />
                            <Link to="/login" className="rp-back">
                                <svg viewBox="0 0 24 24"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
                                Back to Login
                            </Link>
                        </div>
                    )}

                    {/* ── STATE: Token Invalid / Missing ── */}
                    {tokenStatus === 'invalid' && (
                        <div className="text-center">
                            <div className="rp-icon-circle red">
                                <svg viewBox="0 0 24 24">
                                    <circle cx="12" cy="12" r="10" />
                                    <line x1="15" y1="9" x2="9" y2="15" />
                                    <line x1="9" y1="9" x2="15" y2="15" />
                                </svg>
                            </div>
                            <h4 className="rp-title">Invalid Link</h4>
                            <p className="rp-subtitle">
                                This reset link is invalid or has already been used.
                            </p>
                            <div className="rp-expired-badge">
                                Please request a new password reset link from the forgot password page.
                            </div>
                            <Link to="/forget-password" className="rp-btn d-block text-center text-decoration-none mt-3"
                                style={{ padding: '13px', borderRadius: '12px' }}>
                                Request New Link
                            </Link>
                            <hr className="rp-divider" />
                            <Link to="/login" className="rp-back">
                                <svg viewBox="0 0 24 24"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
                                Back to Login
                            </Link>
                        </div>
                    )}

                    {/* ── STATE: Token Valid — Reset Form ── */}
                    {tokenStatus === 'valid' && !resetDone && (
                        <>
                            <div className="text-center mb-4">
                                <div className="rp-icon-circle purple">
                                    <svg viewBox="0 0 24 24">
                                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                    </svg>
                                </div>
                                <h4 className="rp-title">Set New Password</h4>
                                <p className="rp-subtitle">
                                    Create a strong password to secure your account.
                                </p>
                            </div>

                            <form onSubmit={handleSubmit(onSubmit)} noValidate>
                                {/* New Password */}
                                <div className="mb-3">
                                    <label className="rp-label">New Password</label>
                                    <div className="rp-input-wrap">
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            className={`rp-input form-control ${errors.password ? 'has-error' : ''}`}
                                            placeholder="Enter new password"
                                            autoComplete="new-password"
                                            {...register('password')}
                                        />
                                        <button
                                            type="button"
                                            className="rp-eye-btn"
                                            onClick={() => setShowPassword(p => !p)}
                                            tabIndex={-1}
                                        >
                                            <EyeIcon open={showPassword} />
                                        </button>
                                    </div>
                                    {errors.password && (
                                        <div className="rp-error">
                                            <svg width="12" height="12" viewBox="0 0 24 24">
                                                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                                            </svg>
                                            {errors.password.message}
                                        </div>
                                    )}

                                    {/* Strength meter */}
                                    {passwordValue && (
                                        <div className="strength-wrap">
                                            <div className="strength-bars">
                                                {[1, 2, 3, 4].map(i => (
                                                    <div
                                                        key={i}
                                                        className="strength-bar"
                                                        style={{ background: i <= strength.score ? strength.color : undefined }}
                                                    />
                                                ))}
                                            </div>
                                            <span className="strength-label" style={{ color: strength.color }}>
                                                {strength.label}
                                            </span>
                                        </div>
                                    )}

                                    {/* Requirements */}
                                    <ul className="req-list">
                                        {[
                                            { label: 'At least 8 characters', met: passwordValue.length >= 8 },
                                            { label: 'One uppercase letter', met: /[A-Z]/.test(passwordValue) },
                                            { label: 'One number', met: /[0-9]/.test(passwordValue) },
                                            { label: 'One special character', met: /[^A-Za-z0-9]/.test(passwordValue) },
                                        ].map(({ label, met }) => (
                                            <li key={label} className={`req-item ${met ? 'met' : ''}`}>
                                                <span className="req-dot" />
                                                {label}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* Confirm Password */}
                                <div className="mb-4">
                                    <label className="rp-label">Confirm Password</label>
                                    <div className="rp-input-wrap">
                                        <input
                                            type={showConfirm ? 'text' : 'password'}
                                            className={`rp-input form-control ${errors.confirmPassword ? 'has-error' : ''}`}
                                            placeholder="Re-enter your password"
                                            autoComplete="new-password"
                                            {...register('confirmPassword')}
                                        />
                                        <button
                                            type="button"
                                            className="rp-eye-btn"
                                            onClick={() => setShowConfirm(p => !p)}
                                            tabIndex={-1}
                                        >
                                            <EyeIcon open={showConfirm} />
                                        </button>
                                    </div>
                                    {errors.confirmPassword && (
                                        <div className="rp-error">
                                            <svg width="12" height="12" viewBox="0 0 24 24">
                                                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                                            </svg>
                                            {errors.confirmPassword.message}
                                        </div>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    className="rp-btn"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting && <span className="btn-inner-spinner" />}
                                    {isSubmitting ? 'Resetting Password...' : 'Reset Password'}
                                </button>
                            </form>

                            <hr className="rp-divider" />
                            <div className="text-center">
                                <Link to="/login" className="rp-back">
                                    <svg viewBox="0 0 24 24"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
                                    Back to Login
                                </Link>
                            </div>
                        </>
                    )}

                    {/* ── STATE: Reset Successful ── */}
                    {resetDone && (
                        <div className="text-center">
                            <div className="rp-icon-circle green">
                                <svg viewBox="0 0 24 24">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                            </div>
                            <h4 className="rp-title">Password Updated!</h4>
                            <p className="rp-subtitle">
                                Your password has been reset successfully. You can now log in with your new password.
                            </p>
                            <div className="rp-success-email mt-3">
                                ✓ &nbsp;Password changed successfully
                            </div>
                            <Link
                                to="/login"
                                className="rp-btn d-block text-center text-decoration-none mt-3"
                                style={{ padding: '13px', borderRadius: '12px' }}
                            >
                                Go to Login
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default ResetPass;